import type { PaymentMethodIconGlyph } from '@sunwebgroup/uds-ui/payment-method-icon'
import type { BrandName } from '@/scopes/common/domain/types'
import { mapPaymentMethodToGlyph, type PaymentMethod } from '@/scopes/common/domain/payment-methods/payment-methods'
import {
  ADYEN_CREDIT_CARD_GROUP_CODE,
  CREDIT_CARD_PAYMENT_METHOD_KEY,
  PaymentMethodOption,
  TransactionType,
} from '../types'

interface TranslationDescriptor {
  id: string
}

type TranslationResolver = (descriptor: TranslationDescriptor) => string | undefined
type PaymentMethodTranslationSuffix = 'label' | 'secondary_label'

const PAYMENT_METHOD_TRANSLATION_KEYS: Partial<
  Record<PaymentMethod, Partial<Record<PaymentMethodTranslationSuffix, string>>>
> = {
  alma: {
    label: 'payment_scope_method_alma_label',
  },
  banktransfer: {
    label: 'payment_scope_method_banktransfer_label',
  },
  bcmc: {
    label: 'payment_scope_method_bcmc_label',
  },
  bcmc_mobile: {
    label: 'payment_scope_method_bcmc_mobile_label',
  },
  creditcard: {
    label: 'payment_scope_method_creditcard_label',
  },
  klarna: {
    label: 'payment_scope_method_klarna_label',
  },
  klarna_account: {
    label: 'payment_scope_method_klarna_account_label',
  },
  trustly: {
    label: 'payment_scope_method_trustly_label',
  },
  voucher: {
    label: 'payment_scope_method_voucher_label',
  },
}

export interface PaymentMethodRow {
  key: string
  paymentMethodTypes: PaymentMethod[]
  transactionType: TransactionType
  label: string
  secondaryLabel?: string
  glyph?: PaymentMethodIconGlyph
}

function formatLabel(value: string) {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, character => character.toUpperCase())
}

function getTranslatedValue(
  method: PaymentMethodOption,
  suffix: PaymentMethodTranslationSuffix,
  translate?: TranslationResolver,
) {
  const id = PAYMENT_METHOD_TRANSLATION_KEYS[method.paymentMethodType]?.[suffix]

  return id ? translate?.({ id }) : undefined
}

function getMethodLabel(method: PaymentMethodOption, translate?: TranslationResolver) {
  const translatedLabel = getTranslatedValue(method, 'label', translate)

  if (translatedLabel) {
    return translatedLabel
  }

  switch (method.paymentMethodType) {
    case 'ideal':
      return 'iDEAL | Wero'
    case 'visa':
      return 'Visa'
    case 'mastercard':
      return 'Mastercard'
    case 'mc':
      return 'Mastercard'
    case 'amex':
      return 'American Express'
    case 'voucher':
      return 'Voucher'
    case 'banktransfer':
      return 'Bank transfer'
    case 'klarna_account':
      return 'Klarna account'
    case 'klarna':
      return 'Klarna'
    case 'mobilepay':
      return 'MobilePay'
    case 'trustly':
      return 'Trustly'
    case 'paypal':
      return 'PayPal'
    case 'bancontact':
      return 'Bancontact'
    case 'paybybank':
      return 'Pay by bank'
    case 'dinersclub':
      return 'Diners Club'
    case 'discover':
      return 'Discover'
    case 'jcb':
      return 'JCB'
    case 'kbc':
      return 'KBC'
    case 'swish':
      return 'Swish'
    case 'unionpay':
      return 'Union Pay'
    case 'dankort':
      return 'Dankort'
    case 'cartesbancaires':
      return 'Cartes Bancaires'
    case 'giropay':
      return 'Giropay'
    default:
      return formatLabel(method.paymentMethodType)
  }
}

type FeeFormatter = (percentageFee: number, fixedFee: number) => string

function getMethodSecondaryLabel(
  method: PaymentMethodOption,
  translate?: TranslationResolver,
  formatFeeLabel?: FeeFormatter,
) {
  if (
    formatFeeLabel
    && typeof method.percentageFee === 'number'
    && typeof method.fixedFee === 'number'
  ) {
    return formatFeeLabel(method.percentageFee, method.fixedFee)
  }

  return getTranslatedValue(method, 'secondary_label', translate)
}

export function buildPaymentMethodRows(
  paymentMethods: PaymentMethodOption[],
  availablePaymentMethodTypes: PaymentMethod[],
  translate?: TranslationResolver,
  formatFeeLabel?: FeeFormatter,
  brandName?: BrandName,
): PaymentMethodRow[] {
  const rows: PaymentMethodRow[] = []
  const available = new Set(availablePaymentMethodTypes)
  let creditCardRow: PaymentMethodRow | null = null
  const creditCardLabels: string[] = []

  for (const method of paymentMethods) {
    if (!available.has(method.paymentMethodType)) {
      continue
    }

    if (method.groupCode === ADYEN_CREDIT_CARD_GROUP_CODE) {
      const creditCardMethodLabelId = PAYMENT_METHOD_TRANSLATION_KEYS[CREDIT_CARD_PAYMENT_METHOD_KEY]?.label || 'payment_scope_method_creditcard_label'
      if (!creditCardRow) {
        creditCardRow = {
          key: CREDIT_CARD_PAYMENT_METHOD_KEY,
          paymentMethodTypes: [],
          transactionType: method.transactionType,
          label: translate?.({ id: creditCardMethodLabelId }) ?? 'Credit card',
          glyph: mapPaymentMethodToGlyph(CREDIT_CARD_PAYMENT_METHOD_KEY, brandName),
        }
        rows.push(creditCardRow)
      }

      creditCardRow.paymentMethodTypes.push(method.paymentMethodType)
      creditCardLabels.push(getMethodLabel(method, translate))
      if (creditCardLabels.length > 1) {
        creditCardRow.secondaryLabel = creditCardLabels.join(' or ')
      }
      continue
    }

    const secondaryLabel = getMethodSecondaryLabel(method, translate, formatFeeLabel)

    rows.push({
      key: method.paymentMethodType,
      paymentMethodTypes: [method.paymentMethodType],
      transactionType: method.transactionType,
      label: getMethodLabel(method, translate),
      ...(secondaryLabel ? { secondaryLabel } : {}),
      glyph: mapPaymentMethodToGlyph(method.paymentMethodType, brandName),
    })
  }

  return rows
}
